import React, { useState, useEffect, useTransition } from "react";
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { fonts, size, colors } from "../ColorFont/color";
import { url } from "../ConstUrl/Url";
import { useTranslation } from "react-i18next";

const PurchasedPlansSeller = ({ userData, handleUpgradePlans }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const userId = userData?._id;
  const [plansData, setPlansData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (userId) {
      const fetchPurchasedSellerPlans = async () => {
        try {
          const response = await fetch(
            `${url}/api/users/${userId}/purchased-seller-plans`
          );
          if (!response.ok) {
            throw new Error("Failed to fetch purchased seller plans details");
          }
          const data = await response.json();
          setPlansData(data);
          setLoading(false);
        } catch (err) {
          console.error(err);
          setError(err.message);
          setLoading(false);
        }
      };

      fetchPurchasedSellerPlans();
    }
  }, [userId, userData]);

  if (loading) return <div>Loading...</div>;
  if (error) return <div>Error: {error}</div>;

  return (
    <Box sx={{ margin: isMobile ? "8px" : "14px" }}>
      <Box sx={{ marginBottom: isMobile ? 3 : 5 }}>
        <Typography
          sx={{
            fontFamily: fonts.poppins,
            fontSize: isMobile ? 24 : 35,
            fontWeight: 600,
            color: colors.primary_dark,
            textAlign: "center",
            paddingX: isMobile ? 2 : 0,
            textDecoration: "underline",
            textDecorationThickness: "3px", // Controls underline thickness
            textUnderlineOffset: "8px", // Controls distance from text
            marginBottom: 5,
          }}
        >
          {t("purchasedSellerPlans")}
        </Typography>
      </Box>

      <Box>
        <Box
          sx={{
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            justifyContent: isMobile ? "center" : "flex-end", // Center on mobile, right-align on desktop
            alignItems: "center", // Added to ensure vertical centering
            mb: isMobile ? 2 : 5,
            width: "100%", // Full width for proper centering
          }}
        >
          <Typography
            sx={{
              fontFamily: fonts.poppins,
              fontWeight: 700,
              fontSize: isMobile ? size.smallLabel : size.text,
              color: "#fff",
              textTransform: "capitalize",
              letterSpacing: "0.5px",
              padding: isMobile ? "8px 24px" : "10px 20px", // Increased side padding on mobile
              backgroundImage: colors.button,
              borderRadius: 2,
              boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
              display: "flex", // Changed from inline-flex for better mobile behavior
              alignItems: "center",
              justifyContent: "center",
              width: isMobile ? "auto" : "auto", // Auto width instead of 100%
              minWidth: isMobile ? "200px" : "auto", // Minimum width to prevent squeezing
              textAlign: "center",
              transition: "all 0.3s ease-in-out",
              mx: "auto", // Automatic horizontal margins for centering
            }}
          >
            {t("sponsoredHrsRemaining")}: {userData.sponseredHours}
          </Typography>
        </Box>

        <Box>
          {plansData.purchasedSellerPlans.length === 0 ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                height: "60vh",
                gap: 2,
                textAlign: "center",
                padding: 2,
                backgroundImage:
                  "linear-gradient(rgba(255,255,255,0.9), rgba(255,255,255,0.9))",
                borderRadius: 2,
              }}
            >
              <img
                src="chat.png" // Replace with your farming illustration
                alt="Empty farm field"
                style={{
                  width: isMobile ? 150 : 200,
                  opacity: 0.8,
                  marginBottom: 2,
                }}
              />

              <Typography
                sx={{
                  fontFamily: fonts.poppins,
                  fontSize: isMobile ? size.subtitle : size.title,
                  fontWeight: 600,
                  color: colors.primary_dark,
                  mb: 1,
                }}
              >
                {/* Your Plan Stall is Empty */}
                {t("emptyabc")}
              </Typography>

              <Typography
                sx={{
                  fontFamily: fonts.poppins,
                  fontSize: isMobile ? size.smallLabel : size.formLabel,
                  maxWidth: 500,
                  mb: 2,
                  lineHeight: 1.6,
                }}
              >
                {t("notPurchased")}
              </Typography>

              <Button
                variant="contained"
                sx={{
                  fontFamily: fonts.poppins,
                  backgroundImage: colors.button,
                  borderRadius: 2,
                  padding: "8px 20px",
                  fontSize: isMobile ? size.smallLabel : size.formLabel,
                  textTransform: "none",
                  mt: 2,
                  "&:hover": {
                    transform: "translateY(-2px)",
                  },
                }}
                onClick={handleUpgradePlans}
              >
                {t("exploreSellerPlans")}
              </Button>
            </Box>
          ) : (
            <TableContainer
              component={Paper}
              elevation={isMobile ? 1 : 2}
              sx={{
                borderRadius: isMobile ? 2 : 4,
                overflowX: "auto",
              }}
            >
              <Table size={isMobile ? "small" : "medium"}>
                <TableHead>
                  <TableRow>
                    <TableCell
                      sx={{
                        fontWeight: 600,
                        fontFamily: fonts.poppins,
                        fontSize: isMobile ? size.smallLabel : size.formLabel,
                        padding: isMobile ? "8px" : "16px",
                      }}
                    >
                      {t("planName")}
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 600,
                        fontFamily: fonts.poppins,
                        fontSize: isMobile ? size.smallLabel : size.formLabel,
                        padding: isMobile ? "8px" : "16px",
                      }}
                    >
                      {t("discountedPrice")}
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 600,
                        fontFamily: fonts.poppins,
                        fontSize: isMobile ? size.smallLabel : size.formLabel,
                        padding: isMobile ? "8px" : "16px",
                      }}
                    >
                      {t("sponsoredHrs")}
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 600,
                        fontFamily: fonts.poppins,
                        fontSize: isMobile ? size.smallLabel : size.formLabel,
                        padding: isMobile ? "8px" : "16px",
                      }}
                    >
                      {t("purchasedDate")}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {plansData.purchasedSellerPlans.map((plan) => (
                    <TableRow key={plan.planId}>
                      <TableCell
                        sx={{
                          fontFamily: fonts.poppins,
                          fontSize: isMobile ? size.smallLabel : size.formLabel,
                          padding: isMobile ? "8px" : "16px",
                        }}
                      >
                        {plan.name}
                      </TableCell>
                      <TableCell
                        sx={{
                          fontFamily: fonts.poppins,
                          fontSize: isMobile ? size.smallLabel : size.formLabel,
                          padding: isMobile ? "8px" : "16px",
                        }}
                      >
                        ${plan.discountedPrice}
                      </TableCell>
                      <TableCell
                        sx={{
                          fontFamily: fonts.poppins,
                          fontSize: isMobile ? size.smallLabel : size.formLabel,
                          padding: isMobile ? "8px" : "16px",
                        }}
                      >
                        {plan.sponseredHrs}
                      </TableCell>
                      <TableCell
                        sx={{
                          fontFamily: fonts.poppins,
                          fontSize: isMobile ? size.smallLabel : size.formLabel,
                          padding: isMobile ? "8px" : "16px",
                        }}
                      >
                        {new Date(plan.purchaseDate).toLocaleString("en-US", {
                          year: "numeric",
                          month: isMobile ? "short" : "long",
                          day: "numeric",
                          hour: "numeric",
                          minute: "numeric",
                          hour12: true,
                        })}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default PurchasedPlansSeller;
